import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Checkbox, FormControlLabel, Grid, Stack, styled, Switch } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'

import TopicCard from './TopicCard'
import {
  AboType,
  NetworkType,
  PageAboSettings,
  Topic,
  TopicWithMetadata,
  UserAboSettings,
} from '@3m5/sm_publish-frontend-api'
import { DisplayMode, SocialNetworkLicensePermissions } from '@professocial/professocialTypes/types'
import NetworkContentTabs from './NetworkContentTabs'
import { NetworkAboSettings } from '../../../screens/ProfessocialTopics'
import { NavLink } from 'react-router-dom'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import { useRouteMatch } from 'react-router'

const useStyles = makeStyles((theme: Theme) => ({
  text: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  button: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  emailButton: {
    padding: '9px 15px',
  },
  header: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
}))

interface ITopicsOverviewProps {
  data?: TopicWithMetadata[] | undefined
  tabs?: NetworkType[]
  type?: string
  licensePermissions?: SocialNetworkLicensePermissions
  aboSettings?: NetworkAboSettings | undefined
  editable: boolean | undefined
  integrateImages: boolean
  editMode: DisplayMode
  selectedTopic?: (topicId: string) => void
  editSettings?: (topicId: string | undefined, aboType: AboType, networkType?: NetworkType) => void
  editAboSettings?: (newAboSettings: any[], networkType?: NetworkType) => void
  newTopic?: () => void
  deleteTopic?: (topicId: string) => void
  handleTabChange?: (activeTab: NetworkType) => void
  setAcknowledgeSettings?: (topicId: string, applyToDeferredPosts: boolean) => void
}

const AntSwitch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 18,
    height: 18,
    margin: 1,
  },
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
      },
    },
  },
}))

const TopicsOverview: React.FC<ITopicsOverviewProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { path } = useRouteMatch()

  const [aboSettings, setAboSettings] = useState<UserAboSettings | undefined>()
  const [data, setData] = useState<TopicWithMetadata[] | undefined>(props.data)
  const [subscribeAll, setSubscribeAll] = useState<boolean>(false)
  const [publishTypeAll, setPublishTypeAll] = useState<boolean>(false)
  const [activeTab, setActiveTab] = React.useState<NetworkType | undefined>(props.tabs && props.tabs[0])

  const checkGlobalSettings = (settings: UserAboSettings) => {
    let subscribableAboTopicsCount = 0
    settings.topics.map((topic: Topic) => {
      if (topic.active && (topic.manualPostingAllowed || topic.automaticPostingAllowed)) {
        subscribableAboTopicsCount += 1
      }
    })
    let selectedSettingsCounter = 0
    settings.aboSettings.map(elem => {
      if (elem.aboType !== AboType.Off) {
        selectedSettingsCounter += 1
      }
    })
    if (selectedSettingsCounter >= subscribableAboTopicsCount) {
      setPublishTypeAll(true)
      setSubscribeAll(true)
    } else {
      setPublishTypeAll(false)
      setSubscribeAll(false)
    }
    settings.aboSettings.map((setting: PageAboSettings) => {
      if (setting.aboType === AboType.Individual) {
        settings.topics.map((topic: Topic) => {
          if (topic.id === setting.topicId) {
            if (topic.automaticPostingAllowed) {
              setPublishTypeAll(false)
            }
          }
        })
      }
    })
  }

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ProfessocialTopicChangeTab', { detail: activeTab }))
  }, [activeTab])

  useEffect(() => {
    if (props.aboSettings && activeTab && props.aboSettings[activeTab]) {
      checkGlobalSettings(props.aboSettings[activeTab])
      setAboSettings(props.aboSettings[activeTab])
    }
  }, [props.aboSettings, activeTab])

  useEffect(() => {
    props.tabs && !activeTab && setActiveTab(props.tabs[0])
  }, [props.tabs])

  useEffect(() => {
    setData(props.data)
  }, [props.data])

  const changeTempAboSetting = (tempAboSettings: PageAboSettings[]) => {
    props.editAboSettings && props.editAboSettings(tempAboSettings, activeTab)
  }

  const handleSubscribeChange = () => {
    const tempAboSettings: any[] = []
    if (props.aboSettings) {
      if (!subscribeAll) {
        setSubscribeAll(true)
        aboSettings && aboSettings.topics.map((topic) => {
          if (topic.active) {
            if (
              topic.defaultPostingType === AboType.Off &&
              !topic.manualPostingAllowed &&
              !topic.automaticPostingAllowed
            ) {
              return
            }

            const currentAboSetting = aboSettings?.aboSettings.find((item) => item.topicId === topic.id)?.aboType || AboType.Off
            const aboPublishType = currentAboSetting === AboType.Off ? (publishTypeAll ? AboType.Automatic : (topic.manualPostingAllowed ? AboType.Individual : AboType.Automatic)) : currentAboSetting

            tempAboSettings.push({
              topicId: topic.id,
              aboType: aboPublishType,
            })
          }
        })
      } else {
        setSubscribeAll(false)
        setPublishTypeAll(false)

        aboSettings && aboSettings.topics.forEach((topic) => {
          if (topic.noPostingAllowed) {
            if (
              topic.defaultPostingType === AboType.Off &&
              !topic.manualPostingAllowed &&
              !topic.automaticPostingAllowed
            ) {
              return
            }

            tempAboSettings.push({ topicId: topic.id, aboType: AboType.Off })
          }
        })
      }
      changeTempAboSetting(tempAboSettings)
    }
  }

  const handlePublishTypeChange = () => {
    const tempAboSettings: any[] = []
    if (props.aboSettings && activeTab) {
      props.aboSettings && props.aboSettings[activeTab].topics.forEach((topic) => {
        if (!publishTypeAll) {
          if (topic.active && topic.automaticPostingAllowed) {
            tempAboSettings.push({ topicId: topic.id, aboType: AboType.Automatic })
          }
        } else {
          if (topic.active && topic.manualPostingAllowed) {
            tempAboSettings.push({ topicId: topic.id, aboType: AboType.Individual })
          }
        }
      })
      setPublishTypeAll(!publishTypeAll)
      changeTempAboSetting(tempAboSettings)
    }
  }

  return (
    <>
      <>
        {!props.editable && (
          <Grid item container xs={5} className={classes.header}>
            <Stack alignItems='flex-start' justifyContent='center' sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <AntSwitch
                    id='subscribeAll'
                    name='subscribeAll'
                    color='primary'
                    checked={subscribeAll}
                    onChange={() => handleSubscribeChange()}
                  />
                }
                label={subscribeAll ? t('topicsCommon:subscribedAll') as string : t('topicsCommon:subscribeAll') as string}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    id='publishTypeAll'
                    name='publishTypeAll'
                    checked={publishTypeAll}
                    disabled={!subscribeAll}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    onChange={() => handlePublishTypeChange()}
                  />
                }
                label={<Box>{t('topicsCommon:setOnline')}</Box>}
              />
            </Stack>
          </Grid>
        )}
        {props.editable && (
          <Box className={classes.button}>
            <NavLink to={`${path}/add_topic`}>
              <ButtonComponent
                color='primary'
                variant='contained'
                disableElevation
                text={props.type === 'channel' ? t('topicsCommon:createTopic') : t('topicsCommon:createCategory')} />
            </NavLink>
          </Box>
        )}
      </>
      <Grid container spacing={2}>
        {!props.editable && props.tabs && props.tabs.length > 1 &&
          <NetworkContentTabs
            activeItem={activeTab}
            tabs={props.tabs}
            licensePermissions={props.licensePermissions}
            onChangeTab={(tab: NetworkType) => {
              setActiveTab(tab)
              props.handleTabChange && props.handleTabChange(tab)
            }}
          />}
        {!props.editable && aboSettings && aboSettings.topics
          .filter((row: Topic) => !(row.defaultPostingType === AboType.Off && !row.automaticPostingAllowed && !row.manualPostingAllowed))
          .map((topic: Topic, index: number) => {
            if ((topic.active && !topic.isInvisibleForUser)) {
              return (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex' }}>
                  <TopicCard
                    editable={props.editable}
                    data={topic}
                    integrateImages={props.integrateImages}
                    editSettings={(topicId: string | undefined, aboType: AboType) => props.editSettings && props.editSettings(topicId, aboType, activeTab)}
                    editMode={props.editMode}
                    selectTopic={(topicId => props.selectedTopic && props.selectedTopic(topicId))}
                    deleteTopic={(topicId) => props.deleteTopic && props.deleteTopic(topicId)}
                    aboType={aboSettings?.aboSettings.find((item) => item.topicId === topic.id)?.aboType || AboType.Off}
                    settings={aboSettings?.aboSettings.find((item) => item.topicId === topic.id)}
                    setAcknowledgeSettings={props.setAcknowledgeSettings}
                  />
                </Grid>
              )
            }
          })}
        {props.editable && (
          <>
            {data ? (data as Array<TopicWithMetadata | Topic>).map((row: TopicWithMetadata | Topic, index: number) => {
              const rowTopicWithMetadata = row as TopicWithMetadata
              const rowTopic = row as Topic
              const topic: Topic = rowTopicWithMetadata.topic ? rowTopicWithMetadata.topic : rowTopic
              if ((topic.active && !props.editable) || props.editable) {
                return (
                  <Grid key={index} item xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex' }}>
                    <TopicCard
                      editable={props.editable}
                      data={topic}
                      integrateImages={props.integrateImages}
                      editSettings={(topicId: string | undefined, aboType: AboType) => props.editSettings && props.editSettings(topicId, aboType, activeTab)}
                      editMode={props.editMode}
                      selectTopic={(topicId => props.selectedTopic && props.selectedTopic(topicId))}
                      deleteTopic={(topicId) => props.deleteTopic && props.deleteTopic(topicId)}
                      aboType={aboSettings?.aboSettings.find((item) => item.topicId === topic.id)?.aboType || AboType.Off}
                      settings={aboSettings?.aboSettings.find((item) => item.topicId === topic.id)}
                      setAcknowledgeSettings={props.setAcknowledgeSettings}
                    />
                  </Grid>
                )
              }
            }) : <NoDataComponent noDataText={props.type === 'channel' ? t('topicsCommon:noTopics') : t('topicsCommon:noCategories')} alignment="center" margin="16px 0"/>}
          </>
        )}
      </Grid>
    </>
  )
}

export default TopicsOverview
